import { Button, Tooltip } from '@blueprintjs/core';
import cloneDeep from 'lodash/cloneDeep';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useState } from 'react';
import { Button as BsButton, ToastContainer } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { BsLayoutWtf } from 'react-icons/bs';
import Select, { components as selectComponents } from 'react-select';
import type { GroupBase, MultiValue, OptionProps } from 'react-select';

import BayBadge from '../../BayBadge';
import BayCountryIcon from '../../BayCountryIcon';
import BayModal from '../../BayModal';
import BaySelect from '../../BaySelect';
import { BayToastNew } from '../../BayToast';
import type { SelectOption } from '../../types/common';
import {
	getEffectivePageSize,
	matchListingOverlayFormat,
} from '../../utilities/listingOverlay';
import { isSafeZoneElement } from '../../utilities/safeZone';
import { FORMAT_PREVIEW_IMAGES } from '../constants';

import './PageControls.scss';

const createSmartCopyId = (): string => {
	return `smart_copy_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
};

const cloneLayerTreeWithIdsAndMap = (
	layerJson: any,
	idMap: Record<string, string>,
): any => {
	const cloned = cloneDeep(layerJson);
	const sourceId = layerJson?.id;
	cloned.id = createSmartCopyId();
	if (sourceId) {
		idMap[sourceId] = cloned.id;
	}
	if (Array.isArray(cloned.children)) {
		cloned.children = layerJson.children.map((child: any) =>
			cloneLayerTreeWithIdsAndMap(child, idMap),
		);
	}
	return cloned;
};

// Polotno's own Page#clone() (plain "Duplicate page") already assigns fresh ids to the new page
// and every child element, in the same order and shape as the source — so, unlike smart copy above,
// there's no id to capture as it's generated. Walking both trees in lockstep afterwards recovers
// which new id corresponds to which original one.
const pairClonedLayerIds = (
	sourceChildren: any[],
	clonedChildren: any[],
	idMap: Record<string, string>,
): void => {
	if (!Array.isArray(sourceChildren) || !Array.isArray(clonedChildren)) {
		return;
	}
	sourceChildren.forEach((sourceChild, index) => {
		const clonedChild = clonedChildren[index];
		if (!sourceChild?.id || !clonedChild?.id) {
			return;
		}
		idMap[sourceChild.id] = clonedChild.id;
		pairClonedLayerIds(sourceChild.children, clonedChild.children, idMap);
	});
};

// Shared by smart copy and plain page duplication: given an idMap from original element id to its
// copy's id, carries over each copied element's directive so it keeps filling from the same data
// (listing, brand, etc.) as the element it was copied from. `baseDirectives` lets a caller accumulate
// across several idMaps (smart copy targets more than one page per apply) instead of starting over.
const mergeDirectivesForIdMap = (
	sourceDirectives: Record<string, any>,
	idMap: Record<string, string>,
	baseDirectives: Record<string, any> = sourceDirectives,
): Record<string, any> => {
	const nextDirectives = { ...baseDirectives };
	Object.entries(idMap).forEach(([sourceElementId, targetElementId]) => {
		if (!sourceDirectives[sourceElementId]) return;
		nextDirectives[targetElementId] = cloneDeep(sourceDirectives[sourceElementId]);
	});
	return nextDirectives;
};

interface OverlayPage {
	id: string;
	name: string;
	width?: number;
	height?: number;
	ratio?: string;
	previewImage: string | null;
	page: any;
}

type OverlayPageOption = SelectOption<string> & { meta: OverlayPage };

interface ToastData {
	show: boolean;
	variant: string;
	text: string;
}

interface PageControlsProps {
	store: any;
	page: any;
	xPadding: number;
	yPadding: number;
	t: (key: string) => string;
	globalState: any;
	disabled?: boolean;
	translate?: (key: string, params?: Record<string, unknown>) => string;
}

export const PageControls: React.FC<PageControlsProps> = observer(
	({
		store,
		page,
		xPadding,
		yPadding,
		t,
		globalState,
		disabled = false,
		translate = () => '',
	}) => {
		const templateState = globalState.useTemplateState();
		const globalDispatch = globalState.useDispatch();
		const setTemplateState = globalState.setTemplateState;
		const setProjectStateNoAsync = globalState.setProjectStateNoAsync;
		const actingAsState = globalState.useActingAsState();
		const projectState = globalState?.useProjectState
			? globalState?.useProjectState()
			: null; // This only applies to DS app

		const isMultiPage = store.pages.length > 1;
		const pageCount = store.pages.length;
		const index = store.pages.indexOf(page);
		const isListingOverlay = Boolean(templateState?.etc?.isListingOverlay || templateState?.etc?.cfg?.order?.itemId === 'listing-overlays');
		// Determine max pages based on user role and configuration
		// Priority: projectState (DS app) > templateState (Template editor) > default (10)
		const maxPages =
			projectState?.cfg?.customer?.maxPages ||
			(actingAsState?.isGlobalRole && projectState?.cfg?.global?.maxPages) ||
			(actingAsState?.isGlobalRole && templateState?.etc?.cfg?.global?.maxPages) ||
			templateState?.etc?.cfg?.customer?.maxPages ||
			10; // Default fallback
		const pageCountry = actingAsState?.isGlobalRole && page.custom?.country;
		const pageName = page.custom?.name ? `${page.custom.name}:` : `Page: ${index + 1}`;

		const [isSmartCopyOpen, setIsSmartCopyOpen] = useState(false);
		const [copyFromId, setCopyFromId] = useState('');
		const [copyToIds, setCopyToIds] = useState<string[]>([]);
		const [toastData, setToastData] = useState<ToastData>({
			show: false,
			variant: 'warning',
			text: '',
		});

		const showToast = useCallback(
			(toastConfig?: { variant?: string; text?: string }) => {
				setToastData({
					show: true,
					variant: toastConfig?.variant || 'warning',
					text: toastConfig?.text || '',
				});
			},
			[],
		);

		const overlayPages: OverlayPage[] = isListingOverlay
			? (store?.pages || []).map((p: any, idx: number) => {
				const storeJson = store?.toJSON ? store.toJSON() : null;
				const effectiveSize = getEffectivePageSize(storeJson, p);
				const matched = matchListingOverlayFormat(effectiveSize);
				const name = p?.custom?.name || matched?.key || `Page ${idx + 1}`;
				const width = effectiveSize?.width ?? p?.width ?? store?.width;
				const height = effectiveSize?.height ?? p?.height ?? store?.height;
				const ratio = p?.custom?.format;
				return {
					id: p.id,
					name,
					width,
					height,
					ratio,
					previewImage: FORMAT_PREVIEW_IMAGES[idx] || null,
					page: p,
				};
			})
			: [];

		const copyToOptions: OverlayPageOption[] = overlayPages.map((p) => ({
			value: p.id,
			label: p.name,
			meta: p,
		}));

		const copyFromOptions = useMemo<OverlayPageOption[]>(() => {
			return overlayPages.map((p) => ({
				value: p.id,
				label: p.name,
				meta: p,
			}));
		}, [overlayPages]);

		const copyFromSelected = useMemo(() => {
			return copyFromOptions.find((o) => o.value === copyFromId) || null;
		}, [copyFromId, copyFromOptions]);

		const isApplyEnabled = Boolean(copyFromId && copyToIds.length > 0);
		const applyDisabledTooltipText =
			translate('smartCopyApplyDisabledTooltip');

		const closeSmartCopy = useCallback(() => {
			setIsSmartCopyOpen(false);
			setCopyFromId('');
			setCopyToIds([]);
		}, []);

		const openSmartCopy = useCallback(() => {
			setIsSmartCopyOpen(true);
			setCopyFromId(page?.id || '');
			setCopyToIds([]);
		}, [page?.id]);

		const handleChangeCopyFrom = useCallback((nextFromId: string) => {
			setCopyFromId(nextFromId);
			setCopyToIds((prev) => prev.filter((id) => id !== nextFromId));
		}, []);

		const handleApplySmartCopy = useCallback(() => {
			if (!isListingOverlay) return;
			if (!copyFromId || copyToIds.length === 0) return;

			const activePageId = store?.activePage?.id;
			const storeJson = store?.toJSON ? store.toJSON() : null;
			if (!storeJson || !Array.isArray(storeJson.pages)) return;

			const sourcePageJson = storeJson.pages.find((p: any) => p.id === copyFromId);
			const sourceChildrenJsonData = Array.isArray(sourcePageJson?.children)
				? sourcePageJson.children
				: [];
			// Exclude the editor-only safe-zone overlay so Smart Copy never propagates it to target pages.
			const sourceChildrenJson = sourceChildrenJsonData.filter((child: any) => !isSafeZoneElement(child));
			const targetPages = (store?.pages || []).filter((p: any) => copyToIds.includes(p?.id));
			if (targetPages.length === 0 || sourceChildrenJson.length === 0) return;
			const sourceDirectives: Record<string, any> =
				templateState?.etc?.directives || {};
			let nextDirectives = cloneDeep(sourceDirectives);

			store.history.transaction(() => {
				targetPages.forEach((targetPage: any) => {
					const idMap: Record<string, string> = {};
					const copiedChildren = sourceChildrenJson.map((layerJson: any) =>
						cloneLayerTreeWithIdsAndMap(layerJson, idMap),
					);
					copiedChildren.forEach((layerJson: any) => {
						targetPage.addElement(layerJson);
					});
					nextDirectives = mergeDirectivesForIdMap(sourceDirectives, idMap, nextDirectives);
				});
			});
			if (activePageId) {
				store.selectPages([activePageId]);
			}

			globalDispatch(
				setTemplateState({
					etc: {
						...templateState?.etc,
						directives: nextDirectives,
					},
				}),
			);

			showToast({
				variant: 'warning',
				text: translate('smartCopyCopiedToast'),
			});

			closeSmartCopy();
		}, [
			closeSmartCopy,
			copyFromId,
			copyToIds,
			globalDispatch,
			isListingOverlay,
			setTemplateState,
			showToast,
			store,
			templateState?.etc,
			translate,
		]);

		// Same precedence as maxPages above: projectState is set only in the DS app.
		const activeDirectives = projectState
			? projectState?.etc?.directives || {}
			: templateState?.etc?.directives || {};

		const applyDirectivesUpdate = useCallback(
			(nextDirectives: Record<string, any>) => {
				if (projectState) {
					globalDispatch(
						setProjectStateNoAsync({
							etc: { ...projectState?.etc, directives: nextDirectives },
						}),
					);
				} else {
					globalDispatch(
						setTemplateState({
							etc: { ...templateState?.etc, directives: nextDirectives },
						}),
					);
				}
			},
			[globalDispatch, projectState, setProjectStateNoAsync, setTemplateState, templateState?.etc],
		);

		// Page#clone() (the plain "Duplicate page" button) copies elements with brand-new ids and
		// has no directives of its own to carry over, so a duplicated page used to lose every
		// directive-driven field (listing, brand, disclaimer, ...). Pairing the two trees recovers
		// the id mapping so the copy's directives can be filled in the same way smart copy does.
		const handleDuplicatePage = useCallback(() => {
			const sourceChildren = page.toJSON()?.children;
			const clonedPage = page.clone();

			if (Object.keys(activeDirectives).length === 0) {
				return;
			}

			const idMap: Record<string, string> = {};
			pairClonedLayerIds(sourceChildren, clonedPage.toJSON()?.children, idMap);

			if (Object.keys(idMap).length === 0) {
				return;
			}

			applyDirectivesUpdate(mergeDirectivesForIdMap(activeDirectives, idMap));
		}, [activeDirectives, applyDirectivesUpdate, page]);

		const selectedCopyToOptions = useMemo(() => {
			const selected = new Set(copyToIds);
			return copyToOptions.filter((opt) => selected.has(opt.value));
		}, [copyToIds, copyToOptions]);

		const CopyToOption = useMemo(() => {
			const Option = (
				props: OptionProps<OverlayPageOption, true, GroupBase<OverlayPageOption>>,
			) => {
				const isCopyingFrom = props?.data?.value === copyFromId;
				const meta = props?.data?.meta;
				const preview = meta?.previewImage;
				return (
					<selectComponents.Option {...props}>
						<div className="d-flex align-items-center gap-2 bay-smart-copy-option-row">
							<input
								type="checkbox"
								checked={props.isSelected}
								disabled={props.isDisabled}
								readOnly
								className="mt-0 bay-smart-copy-option-checkbox"
							/>
							<div className="bay-smart-copy-option-preview ms-1 border-0">
								{preview && (
									<img
										src={preview}
										alt={translate('smartCopyPreviewAlt', { format: props.label })}
										className="bay-smart-copy-option-preview-image"
									/>
								)}
							</div>
							<div className="d-flex flex-column flex-grow-1">
								<div className="fw-semibold fs-6 bay-smart-copy-option-label mb-1">{props.label}</div>
								<div className="small text-muted bay-smart-copy-option-meta">
									{meta?.width} x {meta?.height} px
									{meta?.ratio ? ` (${meta.ratio})` : ''}
								</div>
							</div>
							{isCopyingFrom && (
								<BayBadge isLight bg="cyan" text="cyan" size="xl">
									{translate('smartCopyCopyingFromBadge')}
								</BayBadge>
							)}
						</div>
					</selectComponents.Option>
				);
			};
			return Option;
		}, [copyFromId, translate]);

		// IF you are not acting as a global role, then you can't change the page order
		const pageOrderIsLocked =
			(!actingAsState?.isGlobalRole &&
				(projectState?.cfg?.pageOrderIsLocked ||
					templateState?.etc?.cfg?.pageOrderIsLocked)) ??
			false;

		const duplicatePageTooltip =
			maxPages && pageCount >= maxPages
				? `${maxPages} ${t('pagesMax')}`
				: `${t('workspace.duplicatePage')} (${maxPages} ${t('pagesMax')})`;
		const insertPageTooltip =
			maxPages && pageCount >= maxPages
				? `${maxPages} ${t('pagesMax')}`
				: `${t('workspace.addPage')} (${maxPages} ${t('pagesMax')})`;

		// Some templates use store-level size (page width/height are 'auto').
		// Listing overlays set page-level sizes, so prefer those for display.
		const activePageWidth = isListingOverlay ? page?.width ?? store.width : store.width;
		const activePageHeight = isListingOverlay ? page?.height ?? store.height : store.height;

		const toast =
			typeof document === 'undefined'
				? null
				: createPortal(
					<ToastContainer
						bsPrefix="toast-container"
						className={`position-fixed p-3 bay-toast-${toastData.variant}`}
						position="top-end"
					>
						<BayToastNew
							delay={3000}
							show={toastData.show}
							variant={toastData.variant}
							text={toastData.text}
							onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
						/>
					</ToastContainer>,
					document.body,
				);

		return (
			<>
				{toast}

				<BayModal
					centered
					closeButton
					show={isSmartCopyOpen}
					title={translate('smartCopyModalTitle')}
					onHideFunc={closeSmartCopy}
					height={520}
					noPadding
					backdrop="static"
					footer={
						<>
							<BsButton variant="outline-secondary" onClick={closeSmartCopy}>
								{translate('cancel')}
							</BsButton>
							<Tooltip content={applyDisabledTooltipText} disabled={isApplyEnabled} position="top">
								<span>
									<BsButton variant="primary" onClick={handleApplySmartCopy} disabled={!isApplyEnabled}>
										{translate('apply')}
									</BsButton>
								</span>
							</Tooltip>
						</>
					}
				>
					<div className="p-5 pt-0 smart-copy-controls">
						<div className="pe-3 mb-5 bay-smart-copy-intro">
							{translate('smartCopyIntroLine1')}
							<br />
							{translate('smartCopyIntroLine2')}
						</div>

						<div className="mb-3">
							<div className="fw-bold mb-2">{translate('smartCopyCopyFrom')}</div>
							<BaySelect
								name="smart_copy_copy_from"
								optionsArray={copyFromOptions}
								controlledValue={copyFromSelected}
								placeholder={translate('select')}
								isClearable={false}
								allowSearch={false}
								onChangeFunc={(opt) =>
									handleChangeCopyFrom((opt as SelectOption<string>)?.value || '')
								}
							/>
						</div>

						<div className="mb-3">
							<div className="fw-bold mb-2">{translate('copyTo')}</div>
							<Select
								isMulti
								className="bay-smart-copy-select"
								classNamePrefix="bay-smart-copy-select"
								placeholder={translate('select')}
								options={copyToOptions}
								value={selectedCopyToOptions}
								onChange={(next: MultiValue<OverlayPageOption>) => {
									const nextIds = (next || []).map((opt) => opt.value);
									setCopyToIds(nextIds.filter((id) => id !== copyFromId));
								}}
								closeMenuOnSelect={false}
								hideSelectedOptions={false}
								isOptionDisabled={(opt) => opt.value === copyFromId}
								components={{ Option: CopyToOption }}
							/>
						</div>
					</div>
				</BayModal>

				{/* print is 300dpi so we show inches instead of the store unit */}
				{(() => {
					const pageSizeLabel = (
						<small>
							{pageCountry && (
								<BayCountryIcon className="me-1" country={pageCountry} />
							)}
							{pageName}{' '}
							{store.dpi === 300
								? `${activePageWidth / 300} x ${activePageHeight / 300} in`
								: `${activePageWidth} x ${activePageHeight} ${store.unit}`}
							{isListingOverlay && page.custom?.format && ` (${page.custom.format})`}
						</small>
					);

					// Listing overlay pages can be as narrow on screen as the Story format
					// (portrait, tall), which doesn't leave enough room for the size label on
					// the left and the Smart Copy Elements button on the right on one row --
					// they'd overlap. Put both in a single wrapping flex row instead of two
					// independently-anchored absolute elements, so the button drops to its own
					// line rather than colliding with the label.
					//
					// `top` + `translateY(-100%)` anchors the row's BOTTOM edge (not its top) a
					// fixed distance above the page, so it grows upward into the padding gutter
					// as it wraps to a second line instead of growing downward into the page --
					// a plain `top` offset was tuned for one line and let a wrapped second line
					// spill onto the canvas.
					return isListingOverlay ? (
						<div
							className="position-absolute d-flex flex-wrap justify-content-between align-items-center gap-1 bay-page-controls-anchor-bottom"
							style={{
								top: `${yPadding - 8}px`,
								left: `${xPadding}px`,
								right: `${xPadding}px`,
							}}
						>
							<div className="text-secondary">{pageSizeLabel}</div>
							<Button
								minimal
								onClick={openSmartCopy}
								icon={<BsLayoutWtf size={15} className="text-muted" />}
								text={<small className="text-muted">{translate('smartCopyElementsButton')}</small>}
							/>
						</div>
					) : (
						<div
							className="position-absolute text-secondary"
							style={{
								top: `${yPadding - 35}px`,
								left: `${xPadding}px`,
							}}
						>
							{pageSizeLabel}
						</div>
					);
				})()}

				{!disabled && !isListingOverlay && (
					<div
						style={{
							position: 'absolute',
							top: `${yPadding - 40}px`,
							right: `${xPadding}px`,
						}}
					>
						{isMultiPage && !pageOrderIsLocked && (
							<Tooltip content={t('workspace.moveUp')} disabled={index === 0}>
								<Button
									minimal
									disabled={index === 0}
									icon="chevron-up"
									onClick={() => {
										page.setZIndex(index - 1);
									}}
								/>
							</Tooltip>
						)}

						{isMultiPage && !pageOrderIsLocked && (
							<Tooltip
								content={t('workspace.moveDown')}
								disabled={index === store.pages.length - 1}
							>
								<Button
									minimal
									disabled={index === store.pages.length - 1}
									icon="chevron-down"
									onClick={() => {
										const index = store.pages.indexOf(page);
										page.setZIndex(index + 1);
									}}
								/>
							</Tooltip>
						)}

						<Tooltip content={duplicatePageTooltip}>
							<Button
								minimal
								disabled={maxPages && pageCount >= maxPages}
								icon="duplicate"
								onClick={handleDuplicatePage}
							/>
						</Tooltip>

						{isMultiPage && (
							<Tooltip content={t('workspace.removePage')}>
								<Button
									minimal
									icon="trash"
									onClick={() => {
										store.deletePages([page.id]);
									}}
								/>
							</Tooltip>
						)}

						<Tooltip content={insertPageTooltip}>
							<Button
								minimal
								disabled={maxPages && pageCount >= maxPages}
								icon="insert"
								onClick={() => {
									const newPage = store.addPage({
										bleed: store.activePage?.bleed || 0,
									});
									const index = store.pages.indexOf(page);
									newPage.setZIndex(index + 1);
								}}
							/>
						</Tooltip>
					</div>
				)}
			</>
		);
	},
);

export default PageControls;
